import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Plus, Trash2 } from 'lucide-react';
import { adminContractsAPI } from '../../services/api';

/**
 * What a new agreement covers and its own work-type rates — the "rate card".
 * Used for each agreement type in Contracts → Settings, and on a contract's
 * Billing in ConnectWise card to adjust one client's agreement before it is
 * created.
 *
 * Covered time is applied to the agreement instead of billed on its own; a
 * work-type rate prices that kind of work on this agreement instead of at its
 * standard rate (Custom = this rate, Multiplier = standard × this, Adjustment =
 * standard + this), with an optional minimum and maximum per charge.
 *
 * `value`: { coverTime, coverProducts, coverExpenses, rates: [{ workTypeId,
 * workTypeName, rateType, rate, hoursMin, hoursMax }] }.
 */
const RATE_TYPES = [['Custom', 'Rate'], ['Multiplier', 'Multiplier'], ['AdjAmount', 'Adjustment']];

export default function RateCardEditor({ value, onChange, disabled = false, allowLoad = true }) {
  const card = { coverTime: false, coverProducts: false, coverExpenses: false, rates: [], ...(value || {}) };
  const [loadId, setLoadId] = useState('');
  const [loadError, setLoadError] = useState('');
  const workTypes = useQuery({ queryKey: ['contract-work-types'], queryFn: () => adminContractsAPI.workTypes().then((r) => r.data.items) });

  const set = (patch) => onChange({ ...card, ...patch });
  const setRate = (i, patch) => set({ rates: card.rates.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const used = new Set(card.rates.map((r) => Number(r.workTypeId)));

  const load = async () => {
    setLoadError('');
    try {
      const r = await adminContractsAPI.rateCardFrom(loadId);
      onChange(r.data.card);
      setLoadId('');
    } catch (e) {
      setLoadError(e.response?.data?.error || 'Could not read that agreement.');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm dark:text-neutral-200">
        <span className="text-neutral-600 dark:text-neutral-300">Covers:</span>
        {[['coverTime', 'Time'], ['coverProducts', 'Products'], ['coverExpenses', 'Expenses']].map(([key, text]) => (
          <label key={key} className="inline-flex items-center gap-2">
            <input type="checkbox" checked={card[key]} disabled={disabled} onChange={(e) => set({ [key]: e.target.checked })} /> {text}
          </label>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-neutral-500 dark:text-neutral-400">
            <tr>
              <th className="py-1 pr-2 font-medium">Work type</th>
              <th className="py-1 pr-2 font-medium w-32">Kind</th>
              <th className="py-1 pr-2 font-medium w-24">Amount</th>
              <th className="py-1 pr-2 font-medium w-20">Min hrs</th>
              <th className="py-1 pr-2 font-medium w-20">Max hrs</th>
              <th className="py-1 pr-2 font-medium w-32">Bill</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {card.rates.map((r, i) => (
              <tr key={i}>
                <td className="py-1 pr-2">
                  <select value={r.workTypeId || ''} disabled={disabled} className="input w-full"
                    onChange={(e) => {
                      const w = (workTypes.data || []).find((x) => x.id === Number(e.target.value));
                      setRate(i, { workTypeId: Number(e.target.value), workTypeName: w?.name || null });
                    }}>
                    <option value="">Choose…</option>
                    {(workTypes.data || []).filter((w) => w.id === Number(r.workTypeId) || !used.has(w.id)).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                    {r.workTypeId && !(workTypes.data || []).some((w) => w.id === Number(r.workTypeId)) && <option value={r.workTypeId}>{r.workTypeName || `#${r.workTypeId}`}</option>}
                  </select>
                </td>
                <td className="py-1 pr-2">
                  <select value={r.rateType || 'Custom'} disabled={disabled} onChange={(e) => setRate(i, { rateType: e.target.value })} className="input w-full">
                    {RATE_TYPES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                  </select>
                </td>
                <td className="py-1 pr-2"><input type="number" min="0" step="0.01" value={r.rate ?? ''} disabled={disabled} onChange={(e) => setRate(i, { rate: e.target.value })} className="input w-full" /></td>
                <td className="py-1 pr-2"><input type="number" min="0" step="0.25" value={r.hoursMin ?? ''} disabled={disabled} onChange={(e) => setRate(i, { hoursMin: e.target.value === '' ? null : e.target.value })} className="input w-full" /></td>
                <td className="py-1 pr-2"><input type="number" min="0" step="0.25" value={r.hoursMax ?? ''} disabled={disabled} onChange={(e) => setRate(i, { hoursMax: e.target.value === '' ? null : e.target.value })} className="input w-full" /></td>
                <td className="py-1 pr-2">
                  <select value={r.billTime || 'Billable'} disabled={disabled} onChange={(e) => setRate(i, { billTime: e.target.value })} className="input w-full">
                    <option value="Billable">Billable</option>
                    <option value="DoNotBill">Do not bill</option>
                    <option value="NoCharge">No charge</option>
                  </select>
                </td>
                <td className="py-1">{!disabled && <button onClick={() => set({ rates: card.rates.filter((_, j) => j !== i) })} className="p-1 text-neutral-400 hover:text-red-600" aria-label="Remove rate"><Trash2 className="w-4 h-4" /></button>}</td>
              </tr>
            ))}
            {!card.rates.length && <tr><td colSpan={7} className="py-2 text-neutral-500">No agreement rates: work bills at its standard rate.</td></tr>}
          </tbody>
        </table>
      </div>

      {!disabled && (
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={() => set({ rates: [...card.rates, { workTypeId: '', rateType: 'Custom', rate: 0, hoursMin: null, hoursMax: null, billTime: 'Billable' }] })} className="text-sm text-primary-600 hover:underline inline-flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Add a work-type rate
          </button>
          {allowLoad && (
            <span className="inline-flex items-center gap-2">
              <input value={loadId} onChange={(e) => setLoadId(e.target.value.replace(/\D/g, ''))} placeholder="Agreement #" className="input w-28" />
              <button disabled={!loadId} onClick={load} className="btn-secondary inline-flex items-center gap-1 text-sm"><Download className="w-4 h-4" /> Copy from that agreement</button>
            </span>
          )}
          {loadError && <span className="text-sm text-red-700">{loadError}</span>}
        </div>
      )}
    </div>
  );
}
